
import { Options, Vue } from 'vue-class-component';
import PageLoading from '@/lib/components/Loading/PageLoading.vue';
import BaseButton from '@/lib/components/Button/BaseButton.vue';
import axios from 'axios';
import { BaseAlert } from '@/lib/components/Banner';
import { DEFAULT_LEGAL_REGION } from '@/constants/legal';
import { LegalPolicyTypes, CustomerBranding } from '@/models';

@Options({
  components: { PageLoading, BaseButton, BaseAlert },
  props: {
    type: {
      type: String,
      validator: (value: string) => Object.values(LegalPolicyTypes).includes(value as LegalPolicyTypes),
      default: LegalPolicyTypes.Privacy
    },
    countryCode: {
      type: String,
      default: DEFAULT_LEGAL_REGION
    },
    /**
     * Force all links to open in a new tab
     * Used when we don't want to take user away from current page - e.g new patient registration
     */
    forceLinksNewTab: {
      type: Boolean,
      default: false
    },
    branding: {
      type: String,
      default: CustomerBranding.BPM
    }
  }
})
export default class LegalPolicy extends Vue {
  legalPolicyHtmls: string[] = [];
  loading = true;
  error = false;
  type!: string;
  branding!: string | null;
  countryCode!: string;
  forceLinksNewTab!: boolean;
  url = 'https://www.bigpicturemedical.com';

  get isEnglishRegion(): boolean{
    return this.countryCode.includes('en') || ['gb', 'us', 'au', 'sg'].includes(this.countryCode);
  }

  async mounted() {
    await this.fetchPolicyHtml();
    this.$watch('countryCode', async () => {
      await this.fetchPolicyHtml();
    });
  }

  getHtmlUrl(): string {
    const baseUrl = process.env.VUE_APP_LEGAL_BASE_URL ?? this.url;
    return `${baseUrl}/${this.type}/`;
  }

  async fetchPolicyHtml() {
    this.loading = true;
    this.error = false;
    this.$emit('fetch-start');

    const brands = [this.branding];

    const allPromises = brands.map(async () => {
      const policy = await this.loadPolicy();
      if (policy) {
        this.legalPolicyHtmls.push(policy.innerHTML);
      }
    });

    const results = await Promise.allSettled(allPromises);
    if (this.legalPolicyHtmls.length > 0) {
      this.$emit('fetch-success');
    } else if (results.some((result) => result.status === 'rejected' && !axios.isCancel(result.reason))) {
      this.error = true;
    }

    this.loading = false;
  }

  async loadPolicy(): Promise<Element | null> {
    const responseData = (await axios.get(this.getHtmlUrl())).data;
    return this.removeHtmlClassAttrs(responseData);
  }

  removeHtmlClassAttrs(htmlElement: string): Element {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(htmlElement, 'text/html');
    const mainContent = parsedHtml.querySelector('#ajax-content-wrap .container-wrap .main-content .wpb_wrapper');
    const element = mainContent.querySelectorAll('div, h2, h3, p, a');
    for (let i = 0; i < element.length; i++) {
      if (element[i].className) {
        element[i].className = '';
      }
    }

    if (this.forceLinksNewTab) {
      const anchorTags = mainContent.getElementsByTagName('a');
      for (let i = 0; i < anchorTags.length; i++) {
        anchorTags[i].setAttribute('target', '_blank');
      }
    }

    return mainContent;
  }
}
